import type {
	AnchorGeneratedFamilyReviewRequestModel,
	AnchorImportFileForm,
	AnchorsDeleteModel,
	AnchorsDeleteResponseModel,
	AnchorStrategyCreateModel,
	AnchorStrategyModel,
	AnchorStrategyUpdateModel,
	AnchorsUpsertModel,
	AnchorsUpsertResponseModel,
	HTTPValidationError,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

export function useImportAnchorAssortmentFromFileMutation(
	options?: UseMutationOptions<
		AnchorsUpsertResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorImportFileForm
	>
) {
	return useMutation<
		AnchorsUpsertResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorImportFileForm
	>({
		mutationFn: (payload) => client.anchors.anchorsImport(payload),
		...(options || {}),
	})
}
export function useUpsertAnchorsMutation(
	options?: UseMutationOptions<
		AnchorsUpsertResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorsUpsertModel
	>
) {
	return useMutation<
		AnchorsUpsertResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorsUpsertModel
	>({
		mutationFn: (payload) => client.anchors.anchorsUpsert(payload),
		...(options || {}),
	})
}

export function useDeleteAnchorsMutation(
	options?: UseMutationOptions<
		AnchorsDeleteResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorsDeleteModel
	>
) {
	return useMutation<
		AnchorsDeleteResponseModel,
		AxiosError<HTTPValidationError>,
		AnchorsDeleteModel
	>({
		mutationFn: (payload) => client.anchors.anchorsDelete(payload),
		...(options || {}),
	})
}
export const useDeleteAnchorStrategyMutation = (
	options?: UseMutationOptions<void, AxiosError<HTTPValidationError>, string>
) =>
	useMutation<void, AxiosError<HTTPValidationError>, string>({
		mutationFn: (anchorStrategyId) =>
			client.anchorStrategies.anchorsAnchorStrategyDelete({ anchorStrategyId }),
		...(options || {}),
	})
export const useCreateAnchorStrategyMutation = (
	options?: UseMutationOptions<
		AnchorStrategyModel,
		AxiosError<HTTPValidationError>,
		AnchorStrategyCreateModel
	>
) =>
	useMutation<
		AnchorStrategyModel,
		AxiosError<HTTPValidationError>,
		AnchorStrategyCreateModel
	>({
		mutationFn: (payload) =>
			client.anchorStrategies.anchorsAnchorStrategyCreate(payload),
		...(options || {}),
	})
export const useUpdateAnchorStrategyMutation = (
	options?: UseMutationOptions<
		AnchorStrategyModel,
		AxiosError<HTTPValidationError>,
		AnchorStrategyUpdateModel & { id: string }
	>
) =>
	useMutation<
		AnchorStrategyModel,
		AxiosError<HTTPValidationError>,
		AnchorStrategyUpdateModel & { id: string }
	>({
		mutationFn: ({ id, ...rest }) =>
			client.anchorStrategies.anchorsAnchorStrategyUpdate(
				{ anchorStrategyId: id },
				rest
			),
		...(options || {}),
	})
export function useAnchorGeneratedFamiliesReviewMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError>,
		AnchorGeneratedFamilyReviewRequestModel
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError>,
		AnchorGeneratedFamilyReviewRequestModel
	>({
		mutationKey: ['anchor-generated-families-review'],
		mutationFn: (payload) =>
			client.anchorStrategies.anchorsGeneratedFamilyReview(payload),
		...(options || {}),
	})
}
